/* eslint-disable react/jsx-props-no-spreading */
import { Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  API_ADGROUP_CREATIVE_ATTACH_URL,
  API_ADGROUP_CREATIVE_DETACH_URL,
  API_ADVERTISERS_CREATIVES_CREATE_URL,
  API_ADVERTISERS_CREATIVES_EDIT_URL,
  API_ADVERTISERS_EDIT_URL,
} from '../../constants/apiUrls';
import { ADVERTISERS_CREATIVES_EDIT_URL, ADVERTISERS_EDIT_URL, EDIT_AD_GROUP_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useConstants } from '../../hooks/useConstants';
import { useSnackBar } from '../../hooks/useSnackBar';
import useVersions, { ModelType } from '../../hooks/useVersions';
import { isAchHost } from '../../util/achAuth';
import { unwrapAchShowModel } from '../../util/achEnvelope';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { deleteRequestJSON, getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import FormEndOptions from '../forms/FormEndOptions';
import Banner from './forms/Banner';
import InlineVideo from './forms/InlineVideo';
import Moment from './forms/Moment';
import Native from './forms/Native';
import Survey from './forms/Survey';
import WrapperVideo from './forms/WrapperVideo';

export enum AssociationType {
  AdGroup,
  Product,
}

const ICA_NATIVE_AD_TYPE = -1;

interface CreativesFormInputs {
  id?: number;
  advertiserId: number;
  adGroupId?: number;
  adType: string;
  link: string;
  size: string;
  category: string;
  properties: string[];
  tag: string;
  subcategory: string;
  variables: string[];
  name?: string;
  deletedAt: string;
  api: string;
  width: number;
  height: number;
  association?: {
    targetedCreativeId: number;
    associated: boolean;
    productIds: any;
  };
  adPreviewId: number;
  icaNativeAd?: {
    placementType: string;
    adResponse: string;
  };
  momentNativeAd?: {
    size: string;
  };
  videoAd?: {
    adTitle: string;
    duration: string;
    rawVideoS3Path?: string;
    adParameters?: string;
    mimes?: string;
    vastAdTagUri?: string;
    adSystem?: string;
  };
  ctrTierInput: boolean;
  appendIasInternalPixel: boolean;
  appendDvInternalPixel: boolean;
  lpu: string;
  thirdPartyTagIds: string;
  customSchedule?: {
    enabled: boolean;
    startDate: string;
    endDate: string;
    defaultStartDate: string;
    defaultEndDate: string;
    publishedAt: string;
  };
  userFreqTtl: string;
  adChoices: boolean;
  generateATag: boolean;
  generateMacros: boolean;
  autoTag: string;
  's3-upload': string;
  uploadType: 'user' | 'capture' | null;
}

export const defaultValues: CreativesFormInputs = {
  id: null,
  advertiserId: null,
  adType: '2',
  link: null,
  size: null,
  category: null,
  properties: null,
  tag: '',
  subcategory: null,
  variables: [],
  name: '',
  deletedAt: null,
  api: '0',
  width: null,
  height: null,
  association: {
    targetedCreativeId: null,
    associated: false,
    productIds: null,
  },
  adPreviewId: null,
  icaNativeAd: null,
  momentNativeAd: null,
  videoAd: null,
  ctrTierInput: true,
  appendIasInternalPixel: false,
  appendDvInternalPixel: false,
  lpu: null,
  thirdPartyTagIds: null,
  customSchedule: {
    enabled: false,
    startDate: null,
    endDate: null,
    defaultStartDate: null,
    defaultEndDate: null,
    publishedAt: null,
  },
  userFreqTtl: null,
  adChoices: true,
  generateATag: false,
  generateMacros: false,
  autoTag: '',
  's3-upload': null,
  uploadType: null,
};

// We have to manually add the advertiser Id in there
// because in edit mode, there is no way for the form to infer what the value is.
const normalizePayload = (advertiserId, form) => {
  // Variables can come through as strings or { value: string } objects depending
  // on how they were entered. Strip empties; send null when nothing remains so
  // the API doesn't receive [''] after the last variable is deleted.
  const normalizedVariables = (form.variables || [])
    .map((v) => (typeof v === 'string' ? v : v?.value))
    .filter((v) => v !== '' && v != null);

  // Send null instead of an empty string for the optional Custom HTML field.
  const normalizedMomentNativeAd = form.momentNativeAd
    ? { ...form.momentNativeAd, autoHtml: form.momentNativeAd.autoHtml || null }
    : form.momentNativeAd;

  const returnValue = {
    ...snakeCaseKeys({
      ...form,
      variables: normalizedVariables.length > 0 ? normalizedVariables : null,
      momentNativeAd: normalizedMomentNativeAd,
      advertiserId,
    }),
    // Don't send uploadType to the server
    uploadType: undefined,
    timeStamp: new Date().getTime(),
  };

  return returnValue;
};

const normalizeForm = (data, targetedCreativeIdValue) => {
  const returnValue = {
    ...camelCaseKeys(data),
    generateMacros: true,
    association: {
      associated: Boolean(targetedCreativeIdValue),
      targetedCreativeId: targetedCreativeIdValue,
    },
  };

  // User image upload vs capture upload detection
  if (data.link) {
    if (data.link.includes('/captured_creative')) {
      returnValue.uploadType = 'capture';
    } else {
      returnValue.uploadType = 'user';
    }
  } else {
    returnValue.uploadType = null;
  }

  return returnValue;
};

interface CreativesFormProps {
  id?: number;
  advertiserId: number;
  adGroupId?: number;
  targetedCreativeId?: number;
  productId?: number;
  contractId?: number;
}

function CreativesForm(props: CreativesFormProps) {
  const formOpenedAt = useRef(Date.now());
  const { showSnackBar } = useSnackBar();
  // The creative SHOW (edit-load) reads route via apiHostFor('creatives') so the
  // per-domain flip retargets them to ACH; every WRITE (create/update/associate)
  // and the advertiser-defaults read stay on the Rails apiHost (creative writes
  // are not migrated). On the ACH leg the show response arrives in the D2 {model}
  // envelope (RD-2), so the edit-load unwraps it with unwrapAchShowModel — the
  // same adaptation the save path already performs on `response.model`.
  const { apiHost, apiHostFor } = useConfig();
  const creativesHost = apiHostFor('creatives');
  const isAchCreativesHost = isAchHost(creativesHost);
  const { constants } = useConstants();
  const [isLoading, setIsLoading] = useState(false);
  const [adTypeForm, setAdTypeForm] = useState(undefined);

  const {
    id: creativeId,
    advertiserId,
    adGroupId,
    targetedCreativeId: originalTargetedCreativeId,
    productId,
    contractId,
  } = props;

  const targetedCreativeId = useRef(originalTargetedCreativeId);

  const isEdit = Boolean(creativeId);

  const INLINE_VIDEO_AD_TYPE = get(constants, 'CREATIVE.INLINE_VIDEO_AD_TYPE');
  const WRAPPER_VIDEO_AD_TYPE = get(constants, 'CREATIVE.WRAPPER_VIDEO_AD_TYPE');
  const MOMENT_NATIVE_AD_TYPE = get(constants, 'CREATIVE.MOMENT_NATIVE_AD_TYPE');
  const MOMENT_NATIVE_SURVEY_AD_TYPE = get(constants, 'CREATIVE.MOMENT_NATIVE_SURVEY_AD_TYPE');

  const form = useForm<CreativesFormInputs>({
    defaultValues,
  });
  const {
    handleSubmit,
    getValues,
    reset,
    resetField,
    watch,
    getFieldState,
    setValue,
    formState: { isDirty: isFormDirty },
  } = form;
  const navigate = useNavigate();
  useConfirmNavChange(() => isFormDirty);

  // Ensure adGroupId is present in form state for downstream consumers (e.g., AutoTag)
  useEffect(() => {
    if (adGroupId) {
      setValue('adGroupId', adGroupId, { shouldDirty: false });
    }
  }, [adGroupId, setValue]);

  // Inherit the IAB category and subcategory from the advertiser when creating a new creative.
  useEffect(() => {
    if (!isEdit) {
      getRequestJSON(apiHost, generatePath(API_ADVERTISERS_EDIT_URL, { id: advertiserId.toString() })).then(
        (response) => {
          if (response && response.id && response.category && response.subcategory) {
            const { category, subcategory } = response;
            resetField('category', { defaultValue: category });
            resetField('subcategory', { defaultValue: subcategory });
          }
        }
      );
    }
  }, [resetField, advertiserId, apiHost, isEdit]);

  // Keeps `targetedCreativeId.current` in step with the association that actually
  // exists on the server: it is what the post-save reset reads to decide whether
  // the "Associated with Ad Group" toggle stays on.
  const updateAssociated = async (newCreativeId) => {
    const payload = snakeCaseKeys({
      creativeId: creativeId || newCreativeId,
      productId: productId || undefined,
      targetedCreativeId: targetedCreativeId.current || undefined,
    });
    const associated = getValues('association.associated');

    try {
      if (associated) {
        const response = await postRequestJSON(
          apiHost,
          generatePath(API_ADGROUP_CREATIVE_ATTACH_URL, { adGroupId: adGroupId?.toString() || '' }),
          payload
        );
        if (response) {
          targetedCreativeId.current = get(camelCaseKeys(response), 'model.association.targetedCreativeId');
        }
      } else if (targetedCreativeId.current) {
        await deleteRequestJSON(
          apiHost,
          generatePath(API_ADGROUP_CREATIVE_DETACH_URL, {
            targetedCreativeId: targetedCreativeId.current.toString(),
            '*': `?${queryString.stringify({
              productId: productId?.toString() || '',
            })}`,
          })
        );
        // The association is gone, so drop the id the detach was aimed at.
        targetedCreativeId.current = null;
      }
    } catch (e) {
      // Attach/detach failed, so the server still holds whatever association it
      // had. Leave the ref alone and let the post-save reset put the toggle back
      // to that state instead of to what the user asked for.
      const errors = get(JSON.parse(e.message), 'errors', []);
      showSnackBar(errors.join(' '), 'error', {
        event: 'creative_associate_error',
        creative_id: creativeId,
        advertiser_id: advertiserId,
      });
    }
  };

  const onSubmit = async (fields) => {
    const payload = normalizePayload(advertiserId, {
      ...fields,
      // If associate is set to true, then associate the creative with the ad group, ie. campaign.
      adGroupId: fields.association?.associated ? adGroupId : undefined,
    });
    let response;
    if (isEdit) {
      response = await putRequestJSON(
        apiHost,
        generatePath(API_ADVERTISERS_CREATIVES_EDIT_URL, {
          creativeId: creativeId.toString(),
          '*': `?${queryString.stringify({
            advertiser_id: advertiserId.toString(),
          })}`,
        }),
        payload
      );
    } else {
      response = await postRequestJSON(
        apiHost,
        generatePath(API_ADVERTISERS_CREATIVES_CREATE_URL, { advertiserId: advertiserId.toString() }),
        payload
      );
    }

    // The server can return `error` or `errors` or `exception`
    let errors = get(response, 'errors');
    if (!errors) {
      errors = get(response, 'error');
    }
    if (!errors) {
      errors = get(response, 'exception');
    }

    if (errors) {
      let messages;
      if (Array.isArray(errors)) {
        messages = errors;
      } else {
        messages = [errors];
      }
      showSnackBar(messages.join(' '), 'error', { event: 'creative_save_error' });
      return;
    }

    const normalizedReponse = camelCaseKeys(response);
    const newCreative = get(normalizedReponse, 'model');
    showSnackBar('Successfully saved', 'success', {
      event: isEdit ? 'creative_updated' : 'creative_created',
      creative_id: newCreative?.id,
      creative_name: newCreative?.name,
      advertiser_id: advertiserId,
      formOpenedAt: formOpenedAt.current,
    });
    const { isDirty: associatedFieldIsDirty } = getFieldState('association.associated');
    if (associatedFieldIsDirty) {
      await updateAssociated(newCreative.id);
    }

    // Reset from the association we know is live on the server rather than from
    // this save's return value. A save that never touched the toggle leaves the
    // association untouched too, and resetting with no id would blank the toggle
    // out even though the creative is still attached (ADOPS-3034).
    const currentTargetedCreativeId = targetedCreativeId.current;
    reset(normalizeForm(response?.model, currentTargetedCreativeId));

    if (!isEdit && newCreative.id) {
      setTimeout(() => {
        navigate(
          generatePath(ADVERTISERS_CREATIVES_EDIT_URL, {
            creativeId: newCreative.id.toString(),
            advertiserId: advertiserId.toString(),
            '*': `?${queryString.stringify({
              adGroupId: adGroupId || '',
              targetedCreativeId: currentTargetedCreativeId || '',
              contractId,
            })}`,
          }),
          {
            replace: true,
          }
        );
      }, 1000);
    }
  };

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      getRequestJSON(
        creativesHost,
        generatePath(API_ADVERTISERS_CREATIVES_EDIT_URL, {
          creativeId: creativeId.toString(),
          '*': `?${queryString.stringify({
            advertiser_id: advertiserId.toString(),
            campaign_id: adGroupId,
            targeted_creative_id: targetedCreativeId.current,
          })}`,
        })
      ).then((rawResponse) => {
        // ACH wraps the show resource in a {model} envelope (RD-2); unwrap it so
        // the edit form populates on the ACH leg. No-op on Rails (bare object).
        const response = unwrapAchShowModel(rawResponse, isAchCreativesHost);
        if (response.error) {
          showSnackBar(response.error, 'error', {
            event: 'creative_load_error',
            creative_id: creativeId,
            advertiser_id: advertiserId,
          });
          return;
        }

        if (response.id) {
          const formValues = normalizeForm(response, targetedCreativeId.current);
          reset({
            ...getValues(),
            ...formValues,
          });
        }

        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupId, isEdit, advertiserId, creativesHost, isAchCreativesHost, creativeId, reset, getValues, showSnackBar]);

  const watchAdWatch = watch('adType');

  const handleImageUpdate = useCallback(
    (newImageUrl: string) => {
      setValue('link', newImageUrl, { shouldDirty: true });
    },
    [setValue]
  );

  useEffect(() => {
    const selectedAdType = Number(getValues('adType'));
    const defaultProps = {
      adGroupId,
      onImageUpdate: handleImageUpdate,
    };

    switch (selectedAdType) {
      case INLINE_VIDEO_AD_TYPE:
        setAdTypeForm(<InlineVideo {...defaultProps} />);
        break;
      case WRAPPER_VIDEO_AD_TYPE:
        setAdTypeForm(<WrapperVideo {...defaultProps} />);
        break;
      case ICA_NATIVE_AD_TYPE:
        setAdTypeForm(<Native {...defaultProps} />);
        break;
      case MOMENT_NATIVE_AD_TYPE:
        setAdTypeForm(<Moment {...defaultProps} />);
        break;
      case MOMENT_NATIVE_SURVEY_AD_TYPE:
        setAdTypeForm(<Survey {...defaultProps} />);
        break;
      default:
        setAdTypeForm(<Banner {...defaultProps} />);
    }
  }, [
    adGroupId,
    getValues,
    watchAdWatch,
    INLINE_VIDEO_AD_TYPE,
    WRAPPER_VIDEO_AD_TYPE,
    MOMENT_NATIVE_AD_TYPE,
    MOMENT_NATIVE_SURVEY_AD_TYPE,
    handleImageUpdate,
  ]);

  const { openVersionsModal } = useVersions(ModelType.Creative, creativeId, 0, 10, {
    targetedCreativeId: targetedCreativeId.current,
  });

  const previousUrl = contractId
    ? `${generatePath(EDIT_AD_GROUP_URL, {
        id: contractId.toString(),
        adGroupId: adGroupId.toString(),
      })}#CREATIVES`
    : generatePath(ADVERTISERS_EDIT_URL, {
        advertiserId: advertiserId.toString(),
      });

  return (
    <Stack pb={9}>
      <Stack p={2}>
        <Stack mt={2} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
          <Stack spacing={3} direction="row" alignItems="center">
            <Typography variant="h5">
              {isEdit ? 'Edit' : 'Create'} Creative {isEdit && `ID ${creativeId}`}
            </Typography>
            <Button variant="text" disabled={!isEdit} onClick={openVersionsModal}>
              See Changelog
            </Button>
          </Stack>
        </Stack>
        {isLoading && (
          <Grid container data-testid="skeleton">
            <Grid item xs={12}>
              <Skeleton sx={{ m: 2 }} variant="rectangular" width="25%" height="25" />
              <Skeleton sx={{ m: 2 }} variant="rectangular" width="95%" height="85vh" />
            </Grid>
          </Grid>
        )}
        {!isLoading && (
          <Stack>
            <Stack p={2} sx={{ background: 'white', borderRadius: '5px' }} spacing={2}>
              <Typography variant="h6">General</Typography>
              <FormProvider {...form}>{adTypeForm}</FormProvider>
            </Stack>
          </Stack>
        )}
      </Stack>
      <FormEndOptions position="fixed">
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button variant="outlined" onClick={() => navigate(previousUrl)}>
            Back
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={!isFormDirty}>
            Save Creative
          </Button>
        </Stack>
      </FormEndOptions>
    </Stack>
  );
}

export default CreativesForm;
